import React, { useState } from 'react';
import { Shield, FileText, Cookie, Eye, Lock, Users, CheckCircle, AlertCircle, Globe, Mail } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const LegalPolicies = () => {
  const [activeSection, setActiveSection] = useState('privacy');

  const sidebarItems = [
    { id: 'privacy', label: 'Privacy Policy', icon: Shield },
    { id: 'terms', label: 'Terms & Services', icon: FileText },
    { id: 'cookies', label: 'Cookies Policy', icon: Cookie }
  ];

  const privacyContent = {
    title: 'Privacy Policy',
    lastUpdated: 'January 2025',
    sections: [
      {
        title: '1. Introduction',
        content: 'At GEWO Health, we are committed to protecting your privacy and ensuring the security of your personal health information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our healthcare platform and services. We comply with HIPAA (Health Insurance Portability and Accountability Act), GDPR (General Data Protection Regulation), and other applicable healthcare privacy regulations.'
      },
      {
        title: '2. Information We Collect',
        subsections: [
          {
            subtitle: '2.1 Personal Information',
            content: 'We collect personal information that you provide directly to us, including:\n• Name, date of birth, gender, and contact information (email, phone number, address)\n• Government-issued identification numbers (for verification purposes)\n• Emergency contact information\n• Insurance information and payment details\n• Username and password for your account'
          },
          {
            subtitle: '2.2 Health Information',
            content: 'We collect Protected Health Information (PHI) necessary for providing healthcare services:\n• Medical history, current conditions, and symptoms\n• Medications, allergies, and immunization records\n• Laboratory test results and diagnostic reports\n• Clinical notes from healthcare providers\n• Appointment history and consultation records\n• Health Genie AI interaction data for personalized care recommendations'
          },
          {
            subtitle: '2.3 Technical Information',
            content: 'We automatically collect certain information when you access our platform:\n• Device information (type, operating system, browser)\n• IP address and location data\n• Usage data (pages visited, features used, time spent)\n• Cookies and similar tracking technologies'
          }
        ]
      },
      {
        title: '3. How We Use Your Information',
        content: 'We use your information for the following purposes:\n• Providing Healthcare Services: To facilitate appointments, consultations, prescriptions, and other medical services\n• Health Genie AI: To provide personalized health recommendations and insights\n• Communication: To send appointment reminders, follow-ups, and important health information\n• Platform Improvement: To analyze usage patterns and enhance user experience\n• Compliance: To meet legal and regulatory requirements\n• Security: To prevent fraud, protect against security threats, and ensure platform integrity\n• Research: With your explicit consent, for anonymized medical research to improve healthcare outcomes'
      },
      {
        title: '4. Information Sharing and Disclosure',
        subsections: [
          {
            subtitle: '4.1 Healthcare Providers',
            content: 'We share your information with healthcare providers you choose to consult, including doctors, specialists, laboratories, and pharmacies involved in your care.'
          },
          {
            subtitle: '4.2 Service Providers',
            content: 'We may share information with trusted third-party service providers who assist in operating our platform, including:\n• Cloud hosting and data storage providers\n• Payment processors\n• Communication service providers\n• Analytics providers\nAll service providers are bound by strict confidentiality agreements and HIPAA Business Associate Agreements (BAAs).'
          },
          {
            subtitle: '4.3 Legal Requirements',
            content: 'We may disclose information when required by law, including:\n• Court orders or legal processes\n• Public health reporting requirements\n• Law enforcement requests with valid legal authority\n• Emergency situations to protect health and safety'
          },
          {
            subtitle: '4.4 What We Never Do',
            content: 'We will NEVER:\n• Sell your personal or health information to third parties\n• Share your information for marketing purposes without explicit consent\n• Disclose your information to unauthorized parties'
          }
        ]
      },
      {
        title: '5. Data Security',
        content: 'We implement comprehensive security measures to protect your information:\n• End-to-end encryption (AES-256) for data at rest and in transit (TLS 1.3)\n• Multi-factor authentication (MFA) for account access\n• Regular security audits and penetration testing\n• 24/7 security monitoring and threat detection\n• Secure cloud infrastructure with SOC 2 compliance\n• Access controls with role-based permissions\n• Regular data backups with disaster recovery protocols\n• Employee training on HIPAA and data security best practices'
      },
      {
        title: '6. Your Privacy Rights',
        subsections: [
          {
            subtitle: '6.1 Access Rights',
            content: 'You have the right to access your personal and health information. You can view your data through your account dashboard or request a complete copy.'
          },
          {
            subtitle: '6.2 Correction Rights',
            content: 'You can update or correct your information at any time through your account settings or by contacting our support team.'
          },
          {
            subtitle: '6.3 Deletion Rights',
            content: 'You can request deletion of your account and associated data. We will honor deletion requests within 30 days, except where we are required by law to retain certain information.'
          },
          {
            subtitle: '6.4 Data Portability',
            content: 'You can export your data in standard, machine-readable formats (PDF, CSV, JSON) for transfer to other healthcare providers or platforms.'
          },
          {
            subtitle: '6.5 Opt-Out Rights',
            content: 'You can opt out of:\n• Marketing communications (while still receiving essential health-related communications)\n• Non-essential data collection\n• Participation in research studies'
          },
          {
            subtitle: '6.6 Complaint Rights',
            content: 'If you believe your privacy rights have been violated, you have the right to file a complaint with:\n• GEWO Health Privacy Officer\n• U.S. Department of Health and Human Services (for HIPAA complaints)\n• Your local data protection authority (for GDPR complaints)'
          }
        ]
      },
      {
        title: '7. Data Retention',
        content: 'We retain your information for as long as necessary to:\n• Provide ongoing healthcare services\n• Comply with legal and regulatory requirements\n• Resolve disputes and enforce agreements\n\nTypical retention periods:\n• Active accounts: Duration of service use plus 7 years (HIPAA requirement)\n• Health records: Minimum 7 years after last interaction (may vary by jurisdiction)\n• Billing records: 7 years from transaction date\n• Anonymized research data: Indefinitely for research purposes\n\nAfter retention periods expire, we securely delete or anonymize your information.'
      },
      {
        title: '8. International Data Transfers',
        content: 'GEWO Health operates primarily in [specify jurisdiction]. If you access our services from outside this jurisdiction, your information may be transferred to, stored, and processed in countries where our servers and service providers operate. We ensure appropriate safeguards are in place, including:\n• Standard Contractual Clauses (SCCs) for GDPR compliance\n• Adequate data protection agreements\n• Encryption during transfer and storage'
      },
      {
        title: '9. Children\'s Privacy',
        content: 'GEWO Health services are not intended for children under 13 without parental or guardian consent. We do not knowingly collect information from children under 13 without verified parental consent. If you are a parent/guardian and believe your child has provided information without consent, please contact us immediately to have the information removed.'
      },
      {
        title: '10. Changes to This Privacy Policy',
        content: 'We may update this Privacy Policy periodically to reflect changes in our practices, technology, or legal requirements. We will:\n• Notify you of material changes via email and in-app notification\n• Post the updated policy with a revised "Last Updated" date\n• Provide 30 days\' notice before implementing material changes\n• Request renewed consent where required by law\n\nContinued use of our services after changes constitute acceptance of the updated policy.'
      },
      {
        title: '11. Contact Information',
        content: 'For privacy-related questions, concerns, or to exercise your rights, contact us:\n\nGEWO Health Privacy Officer\nEmail: privacy@gewohealth.com\nPhone: +1 (555) 123-4567\nAddress: 123 Healthcare Ave, Medical District, [City, State, ZIP]\n\nFor HIPAA-specific complaints:\nU.S. Department of Health and Human Services\nOffice for Civil Rights\nWebsite: www.hhs.gov/ocr/privacy/hipaa/complaints'
      }
    ]
  };

  const termsContent = {
    title: 'Terms & Services',
    lastUpdated: 'January 2025',
    sections: [
      {
        title: '1. Acceptance of Terms',
        content: 'Welcome to GEWO Health. By accessing or using our platform, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our services. These Terms constitute a legally binding agreement between you ("User," "you," or "your") and GEWO Health ("we," "us," or "our").'
      },
      {
        title: '2. Description of Services',
        content: 'GEWO Health provides a comprehensive digital healthcare platform offering:\n• Online medical consultations via video, audio, and chat\n• Appointment scheduling with healthcare providers\n• Electronic health records (EHR) management\n• Prescription services and medication management\n• Health Genie AI for personalized health recommendations\n• Specialized services including elderly care, women\'s health, and lifestyle disease management\n• Laboratory test booking and results delivery\n• Surgical procedure coordination and management\n• Corporate healthcare solutions for organizations'
      },
      {
        title: '3. Eligibility and Account Registration',
        subsections: [
          {
            subtitle: '3.1 Eligibility',
            content: 'To use GEWO Health services, you must:\n• Be at least 18 years old (or have parental/guardian consent if under 18)\n• Have legal capacity to enter into binding contracts\n• Provide accurate and complete registration information\n• Comply with all applicable laws in your jurisdiction'
          },
          {
            subtitle: '3.2 Account Security',
            content: 'You are responsible for:\n• Maintaining the confidentiality of your login credentials\n• All activities that occur under your account\n• Notifying us immediately of unauthorized access\n• Using strong passwords and enabling multi-factor authentication (MFA)\n\nWe are not liable for losses resulting from unauthorized account use that you could have prevented.'
          },
          {
            subtitle: '3.3 Account Accuracy',
            content: 'You agree to:\n• Provide true, accurate, current, and complete information\n• Update your information promptly when it changes\n• Not impersonate another person or entity\n• Not create multiple accounts without authorization'
          }
        ]
      },
      {
        title: '4. Healthcare Services and Limitations',
        subsections: [
          {
            subtitle: '4.1 Not Emergency Services',
            content: 'GEWO Health is NOT for medical emergencies. In case of emergency, call your local emergency number (911 in the US) or go to the nearest emergency room immediately. Our platform should not be used for:\n• Life-threatening conditions\n• Severe injuries\n• Chest pain or heart attack symptoms\n• Stroke symptoms\n• Severe bleeding\n• Poisoning\n• Suicidal thoughts or behavior'
          },
          {
            subtitle: '4.2 Telemedicine Limitations',
            content: 'You understand that telemedicine has limitations:\n• Physical examination is limited to visual assessment\n• Diagnostic accuracy may be reduced compared to in-person visits\n• Technical issues may interrupt consultations\n• Not all conditions can be adequately assessed remotely\n\nYour healthcare provider will advise if an in-person visit is necessary.'
          },
          {
            subtitle: '4.3 Provider-Patient Relationship',
            content: 'Healthcare providers on GEWO Health are independent professionals. The provider-patient relationship is established directly between you and the provider. GEWO Health:\n• Facilitates connections but does not practice medicine\n• Does not guarantee specific outcomes or results\n• Is not responsible for provider actions or medical decisions\n• Verifies provider credentials but does not supervise clinical care'
          },
          {
            subtitle: '4.4 Health Genie AI Disclaimer',
            content: 'Health Genie AI provides educational information and general health recommendations. It is NOT a substitute for professional medical advice, diagnosis, or treatment. Always consult a licensed healthcare provider for medical concerns. Health Genie AI:\n• Cannot diagnose medical conditions\n• Cannot prescribe medications\n• Should not be relied upon for emergency situations\n• Provides suggestions based on algorithms and may not be accurate'
          }
        ]
      },
      {
        title: '5. User Conduct and Responsibilities',
        content: 'You agree NOT to:\n• Violate any applicable laws or regulations\n• Infringe on intellectual property rights\n• Upload false, misleading, or inaccurate information\n• Transmit viruses, malware, or harmful code\n• Attempt to gain unauthorized access to our systems\n• Harass, abuse, or threaten healthcare providers or staff\n• Use the platform for illegal activities\n• Share your account credentials with others\n• Scrape, data mine, or extract data from the platform\n• Use automated systems (bots) without authorization\n• Interfere with platform operations or security measures'
      },
      {
        title: '6. Fees and Payment',
        subsections: [
          {
            subtitle: '6.1 Service Fees',
            content: 'Fees for services are displayed before booking. You agree to pay all applicable fees, including:\n• Consultation fees\n• Prescription fees\n• Laboratory test fees\n• Subscription fees (if applicable)\n• Administrative fees'
          },
          {
            subtitle: '6.2 Payment Processing',
            content: 'Payments are processed securely through third-party payment processors. You authorize us to charge your selected payment method. We do not store your full credit card information.'
          },
          {
            subtitle: '6.3 Refunds and Cancellations',
            content: 'Cancellation and refund policies:\n• Appointments canceled more than 24 hours in advance: Full refund\n• Appointments canceled less than 24 hours in advance: 50% cancellation fee\n• No-shows: No refund\n• Technical issues on our end: Full refund\n• Dissatisfaction with care quality: Review on a case-by-case basis\n\nRefunds are processed within 7-10 business days.'
          },
          {
            subtitle: '6.4 Insurance',
            content: 'If we accept your insurance, you are responsible for:\n• Verifying coverage before appointments\n• Providing accurate insurance information\n• Paying co-pays, deductibles, and non-covered services\n• Any charges denied by your insurance'
          }
        ]
      },
      {
        title: '7. Intellectual Property Rights',
        content: 'All content on GEWO Health platform, including text, graphics, logos, images, software, and functionality, is owned by GEWO Health or licensed to us and protected by copyright, trademark, and other intellectual property laws. You may not:\n• Copy, modify, or distribute our content without permission\n• Reverse engineer or decompile our software\n• Remove copyright or proprietary notices\n• Use our trademarks without authorization\n\nYou retain ownership of your health information and content you upload, but grant us a license to use it for providing services.'
      },
      {
        title: '8. Privacy and Data Protection',
        content: 'Your use of GEWO Health is governed by our Privacy Policy, which is incorporated into these Terms by reference. We are committed to protecting your privacy and complying with HIPAA, GDPR, and other applicable data protection laws. Please review our Privacy Policy to understand how we collect, use, and protect your information.'
      },
      {
        title: '9. Disclaimers and Limitation of Liability',
        subsections: [
          {
            subtitle: '9.1 Service Disclaimer',
            content: 'GEWO Health platform is provided "AS IS" and "AS AVAILABLE" without warranties of any kind, express or implied, including but not limited to:\n• Warranties of merchantability or fitness for a particular purpose\n• Warranties of uninterrupted or error-free service\n• Warranties regarding accuracy or reliability of information\n• Warranties that defects will be corrected'
          },
          {
            subtitle: '9.2 Limitation of Liability',
            content: 'To the maximum extent permitted by law, GEWO Health shall not be liable for:\n• Indirect, incidental, consequential, or punitive damages\n• Loss of profits, data, or business opportunities\n• Medical outcomes or provider decisions\n• Technical failures or service interruptions\n• Third-party actions or content\n\nOur total liability shall not exceed the amount you paid us in the 12 months preceding the claim.'
          },
          {
            subtitle: '9.3 Medical Disclaimer',
            content: 'GEWO Health does not practice medicine or provide medical advice. Healthcare services are provided by independent licensed professionals. We are not responsible for:\n• Medical diagnoses or treatment decisions\n• Outcomes of medical care\n• Provider negligence or malpractice\n• Adverse reactions to medications or treatments'
          }
        ]
      },
      {
        title: '10. Indemnification',
        content: 'You agree to indemnify, defend, and hold harmless GEWO Health, its affiliates, officers, directors, employees, and agents from any claims, liabilities, damages, losses, costs, or expenses (including legal fees) arising from:\n• Your violation of these Terms\n• Your violation of any law or third-party rights\n• Your use or misuse of the platform\n• Content you submit or upload\n• Your interactions with healthcare providers'
      },
      {
        title: '11. Termination',
        content: 'We may suspend or terminate your account immediately, without prior notice, if:\n• You violate these Terms\n• You engage in fraudulent or illegal activity\n• You abuse healthcare providers or staff\n• Required by law or regulatory authority\n• We cease operations\n\nYou may terminate your account at any time through account settings. Upon termination:\n• Your access to the platform will be revoked\n• Outstanding fees remain due\n• We may retain certain information as required by law\n• Provisions that should survive termination will remain in effect'
      },
      {
        title: '12. Dispute Resolution',
        subsections: [
          {
            subtitle: '12.1 Informal Resolution',
            content: 'Before filing a claim, you agree to contact us at legal@gewohealth.com to attempt informal resolution. We will work in good faith to resolve disputes within 60 days.'
          },
          {
            subtitle: '12.2 Arbitration',
            content: 'If informal resolution fails, disputes will be resolved through binding arbitration under the American Arbitration Association (AAA) rules, except:\n• Small claims court disputes (under $10,000)\n• Intellectual property disputes\n• Emergency injunctive relief\n\nArbitration will be conducted individually; class actions are waived.'
          },
          {
            subtitle: '12.3 Governing Law',
            content: 'These Terms are governed by the laws of [State/Country], without regard to conflict of law principles. Any arbitration or court proceedings will be held in [City, State].'
          }
        ]
      },
      {
        title: '13. Changes to Terms',
        content: 'We may modify these Terms at any time. We will:\n• Notify you of material changes via email and in-app notification\n• Post updated Terms with a revised "Last Updated" date\n• Provide 30 days\' notice for material changes\n• Require acceptance of new Terms for continued use\n\nContinued use after changes constitutes acceptance of the new Terms.'
      },
      {
        title: '14. General Provisions',
        content: '• Entire Agreement: These Terms, together with the Privacy Policy, constitute the entire agreement between you and GEWO Health.\n• Severability: If any provision is found unenforceable, the remaining provisions remain in effect.\n• Waiver: Failure to enforce any provision does not waive our right to enforce it later.\n• Assignment: You may not assign these Terms without our consent. We may assign them without restriction.\n• Force Majeure: We are not liable for delays or failures due to circumstances beyond our control (natural disasters, pandemics, wars, etc.).\n• Language: In case of translation discrepancies, the English version prevails.'
      },
      {
        title: '15. Contact Information',
        content: 'For questions about these Terms, contact us:\n\nGEWO Health Legal Department\nEmail: legal@gewohealth.com\nPhone: +1 (555) 123-4567\nAddress: 123 Healthcare Ave, Medical District, [City, State, ZIP]'
      }
    ]
  };

  const cookiesContent = {
    title: 'Cookies Policy',
    lastUpdated: 'January 2025',
    sections: [
      {
        title: '1. What Are Cookies?',
        content: 'Cookies are small text files stored on your device (computer, smartphone, tablet) when you visit websites. They help websites remember your preferences, login status, and browsing behavior to provide a better user experience. Cookies contain information such as:\n• User preferences and settings\n• Session identifiers\n• Authentication tokens\n• Language and region preferences\n• Analytics and usage data\n\nCookies do not contain personal information unless you explicitly provide it, and they cannot access other files on your device or spread viruses.'
      },
      {
        title: '2. Why We Use Cookies',
        content: 'GEWO Health uses cookies and similar tracking technologies to:\n• Enable essential platform functionality (login, security, session management)\n• Remember your preferences and settings\n• Analyze platform usage and performance\n• Improve user experience and personalize content\n• Ensure platform security and prevent fraud\n• Comply with legal and regulatory requirements\n• Provide relevant health information and recommendations\n\nSome cookies are essential for the platform to function properly, while others enhance your experience or help us improve our services.'
      },
      {
        title: '3. Types of Cookies We Use',
        subsections: [
          {
            subtitle: '3.1 Essential Cookies (Always Active)',
            content: 'These cookies are necessary for the platform to function and cannot be disabled:\n• Authentication Cookies: Keep you logged in during your session\n• Security Cookies: Protect against fraudulent activity and ensure secure data transmission\n• Load Balancing Cookies: Distribute traffic efficiently across servers\n• Session Cookies: Maintain your session state and preferences\n\nWithout these cookies, you cannot use core platform features like booking appointments or accessing your health records.'
          },
          {
            subtitle: '3.2 Functional Cookies',
            content: 'These cookies enable enhanced functionality and personalization:\n• Language and region preferences\n• Accessibility settings (font size, contrast)\n• Recently viewed doctors or services\n• Customized dashboard layout\n• Notification preferences\n\nDisabling functional cookies may limit some features but won\'t prevent basic platform use.'
          },
          {
            subtitle: '3.3 Analytics and Performance Cookies',
            content: 'These cookies help us understand how visitors use our platform:\n• Page views and navigation paths\n• Time spent on pages\n• Error messages encountered\n• Device and browser information\n• Geographic location (country/city level)\n\nWe use this data to:\n• Identify and fix technical issues\n• Improve platform design and usability\n• Understand which features are most popular\n• Optimize performance and loading times\n\nAnalytics cookies collect aggregated, anonymous data. We use tools like Google Analytics (with IP anonymization) to process this information.'
          },
          {
            subtitle: '3.4 Marketing and Advertising Cookies',
            content: 'These cookies track your interests to show relevant health information and services:\n• Health topics you\'ve viewed\n• Services you\'ve searched for\n• Providers you\'ve considered\n• Content recommendations based on your health interests\n\nWe do NOT:\n• Share your health information with advertisers\n• Use cookies for third-party advertising networks\n• Display ads from external companies\n\nYou can opt out of marketing cookies without affecting platform functionality.'
          }
        ]
      },
      {
        title: '4. Third-Party Cookies',
        content: 'Some cookies are set by third-party services we use to operate our platform:\n\n• Google Analytics: Website traffic and usage analysis (analytics cookies)\n• Payment Processors (e.g., Stripe): Secure payment processing (essential cookies)\n• Video Conferencing (e.g., Zoom, WebRTC): Enable telemedicine consultations (essential cookies)\n• Cloud Storage (e.g., AWS, Google Cloud): Host platform infrastructure (essential cookies)\n• Content Delivery Networks (CDNs): Deliver content quickly and securely (performance cookies)\n\nThese third parties may set their own cookies subject to their privacy policies. We ensure all third-party services comply with HIPAA and GDPR requirements through Business Associate Agreements (BAAs) and Data Processing Agreements (DPAs).'
      },
      {
        title: '5. How Long Cookies Last',
        content: 'Cookies have different lifespans:\n\n• Session Cookies: Deleted when you close your browser (used for login sessions and temporary preferences)\n• Persistent Cookies: Remain on your device for a set period or until you delete them\n\nTypical cookie durations:\n• Authentication cookies: Session or up to 7 days (if "Remember Me" is enabled)\n• Preference cookies: 1 year\n• Analytics cookies: 2 years\n• Marketing cookies: 1 year\n\nYou can delete cookies at any time through your browser settings.'
      },
      {
        title: '6. Managing Your Cookie Preferences',
        subsections: [
          {
            subtitle: '6.1 Cookie Consent Banner',
            content: 'When you first visit GEWO Health, you\'ll see a cookie consent banner allowing you to:\n• Accept all cookies\n• Reject non-essential cookies\n• Customize cookie preferences by category\n\nYou can change your preferences at any time through the cookie settings link in the footer.'
          },
          {
            subtitle: '6.2 Browser Settings',
            content: 'You can control cookies through your browser settings:\n\n• Google Chrome: Settings > Privacy and Security > Cookies and other site data\n• Firefox: Settings > Privacy & Security > Cookies and Site Data\n• Safari: Preferences > Privacy > Manage Website Data\n• Edge: Settings > Cookies and site permissions\n\nYou can:\n• Block all cookies (may break platform functionality)\n• Block third-party cookies only\n• Delete existing cookies\n• Set cookies to expire when you close the browser\n\nNote: Blocking essential cookies will prevent you from using core platform features.'
          },
          {
            subtitle: '6.3 Opt-Out Links',
            content: 'You can opt out of specific tracking services:\n• Google Analytics: https://tools.google.com/dlpage/gaoptout\n• Do Not Track (DNT): Enable in browser settings (we honor DNT signals)\n\nOpting out does not delete existing cookies; you must also clear your browser cookies.'
          }
        ]
      },
      {
        title: '7. Other Tracking Technologies',
        subsections: [
          {
            subtitle: '7.1 Web Beacons (Pixels)',
            content: 'Small invisible images embedded in emails or web pages that:\n• Track email opens and clicks\n• Monitor page views and user engagement\n• Verify email delivery\n\nWeb beacons work with cookies to track user behavior.'
          },
          {
            subtitle: '7.2 Local Storage',
            content: 'Browser storage that holds data locally on your device:\n• HTML5 local storage\n• Session storage\n• IndexedDB\n\nWe use local storage for:\n• Caching health records for faster access\n• Storing user preferences\n• Enabling offline functionality\n\nLocal storage can hold more data than cookies and doesn\'t expire automatically. You can clear it through browser settings.'
          },
          {
            subtitle: '7.3 Device Fingerprinting',
            content: 'We may collect device information for security purposes:\n• Device type and model\n• Operating system and version\n• Screen resolution\n• Browser type and version\n• Installed fonts and plugins\n\nThis helps us:\n• Detect and prevent fraudulent activity\n• Identify suspicious login attempts\n• Ensure platform compatibility\n\nDevice fingerprinting does not track you across websites.'
          }
        ]
      },
      {
        title: '8. Cookies and Health Data Privacy',
        content: 'We take special care with cookies that may access health information:\n• Health-related cookies are encrypted and secured\n• Sensitive health data is never stored in cookies\n• Cookies containing health information are session-only and deleted when you log out\n• We minimize health data in cookies, storing only necessary identifiers\n• All cookies comply with HIPAA and GDPR requirements\n\nYour health records, diagnoses, medications, and clinical notes are stored securely on our servers with encryption, not in cookies.'
      },
      {
        title: '9. Children\'s Privacy',
        content: 'GEWO Health does not knowingly collect data from children under 13 through cookies without parental consent. If a parent or guardian uses our platform on behalf of a minor, cookies will be associated with the parent/guardian\'s account. We comply with COPPA (Children\'s Online Privacy Protection Act) requirements.'
      },
      {
        title: '10. International Data Transfers',
        content: 'Cookies may transfer data to servers located outside your country. We ensure adequate protection through:\n• Standard Contractual Clauses (SCCs) for GDPR compliance\n• Encryption during data transfer and storage\n• Data Processing Agreements with third-party services\n• Compliance with local data protection laws\n\nYour consent to cookies includes consent to international data transfers necessary for platform operation.'
      },
      {
        title: '11. Updates to This Cookie Policy',
        content: 'We may update this Cookie Policy periodically to reflect:\n• Changes in cookie usage\n• New tracking technologies\n• Legal or regulatory requirements\n• Platform improvements\n\nWe will:\n• Notify you of material changes via email or in-app notification\n• Update the "Last Updated" date\n• Provide 30 days\' notice for significant changes\n• Re-prompt for consent if required by law\n\nContinued use after changes constitutes acceptance of the updated policy.'
      },
      {
        title: '12. Contact Information',
        content: 'For questions about cookies or to exercise your rights, contact us:\n\nGEWO Health Privacy Team\nEmail: privacy@gewohealth.com\nPhone: +1 (555) 123-4567\nAddress: 123 Healthcare Ave, Medical District, [City, State, ZIP]\n\nFor EU/UK users:\nData Protection Officer (DPO)\nEmail: dpo@gewohealth.com\n\nYou can also manage your cookie preferences through our cookie settings tool available in the footer of every page.'
      }
    ]
  };

  const renderContent = () => {
    let content;
    switch (activeSection) {
      case 'privacy':
        content = privacyContent;
        break;
      case 'terms':
        content = termsContent;
        break;
      case 'cookies':
        content = cookiesContent;
        break;
      default:
        content = privacyContent;
    }

    return (
      <div style={styles.contentArea}>
        <div style={styles.contentHeader}>
          <h1 style={styles.contentTitle}>{content.title}</h1>
          <p style={styles.lastUpdated}>Last Updated: {content.lastUpdated}</p>
        </div>

        <div style={styles.contentBody}>
          {content.sections.map((section, index) => (
            <div key={index} style={styles.section}>
              <h2 style={styles.sectionTitle}>{section.title}</h2>
              {section.content && (
                <p style={styles.sectionContent}>
                  {section.content.split('\n').map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      {i < section.content.split('\n').length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </p>
              )}
              {section.subsections && section.subsections.map((subsection, subIndex) => (
                <div key={subIndex} style={styles.subsection}>
                  <h3 style={styles.subsectionTitle}>{subsection.subtitle}</h3>
                  <p style={styles.subsectionContent}>
                    {subsection.content.split('\n').map((line, i) => (
                      <React.Fragment key={i}>
                        {line}
                        {i < subsection.content.split('\n').length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </p>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div style={styles.contactCTA}>
          <Mail size={32} color="#133E97" />
          <h3 style={styles.ctaTitle}>Questions about our policies?</h3>
          <p style={styles.ctaText}>
            Our team is here to help clarify any concerns you may have.
          </p>
          <button style={styles.ctaButton}>Contact Legal Team</button>
        </div>
      </div>
    );
  };

  return (
    <div style={styles.page}>
      <Navbar />
      
      {/* Hero Header */}
      <section style={styles.heroSection}>
        <div style={styles.heroContainer}>
          <div style={styles.heroContent}>
            <div style={styles.heroBadge}>
              <Shield size={20} />
              <span>Legal & Privacy</span>
            </div>
            <h1 style={styles.heroTitle}>Legal Policies</h1>
            <p style={styles.heroSubtitle}>
              Transparency and trust are at the core of GEWO Health. Review our comprehensive 
              legal policies to understand how we protect your rights and data.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <section style={styles.mainSection}>
        <div style={styles.mainContainer}>
          <div style={styles.layoutGrid}>
            {/* Sidebar Navigation */}
            <aside style={styles.sidebar}>
              <div style={styles.sidebarSticky}>
                <h3 style={styles.sidebarTitle}>Legal Documents</h3>
                <nav style={styles.sidebarNav}>
                  {sidebarItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeSection === item.id;
                    return (
                      <button
                        key={item.id}
                        onClick={() => setActiveSection(item.id)}
                        style={{
                          ...styles.sidebarItem,
                          ...(isActive ? styles.sidebarItemActive : {})
                        }}
                      >
                        <Icon size={20} />
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </nav>

                {/* Quick Info Box */}
                <div style={styles.quickInfo}>
                  <AlertCircle size={20} color="#FFB800" />
                  <p style={styles.quickInfoText}>
                    These policies are effective as of January 2025 and apply to all users of GEWO Health services.
                  </p>
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <main style={styles.mainContent}>
              {renderContent()}
            </main>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background: '#FFFFFF'
  },

  // Hero Section
  heroSection: {
    background: 'linear-gradient(135deg, #133E97 0%, #1a52c9 100%)',
    paddingTop: '100px',
    paddingBottom: '60px',
    color: 'white'
  },
  heroContainer: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 24px'
  },
  heroContent: {
    maxWidth: '700px'
  },
  heroBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    background: 'rgba(255, 255, 255, 0.2)',
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '20px',
    backdropFilter: 'blur(10px)'
  },
  heroTitle: {
    fontSize: '48px',
    fontWeight: '800',
    marginBottom: '16px',
    lineHeight: '1.2'
  },
  heroSubtitle: {
    fontSize: '18px',
    lineHeight: '1.6',
    opacity: 0.95
  },

  // Main Section
  mainSection: {
    padding: '60px 0 80px',
    background: '#f8f9fa'
  },
  mainContainer: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '0 24px'
  },
  layoutGrid: {
    display: 'grid',
    gridTemplateColumns: '320px 1fr',
    gap: '40px',
    alignItems: 'start'
  },

  // Sidebar
  sidebar: {
    position: 'relative'
  },
  sidebarSticky: {
    position: 'sticky',
    top: '100px'
  },
  sidebarTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#133E97',
    marginBottom: '16px',
    letterSpacing: '0.5px',
    textTransform: 'uppercase'
  },
  sidebarNav: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '32px'
  },
  sidebarItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 16px',
    background: 'white',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: '600',
    color: '#133E97',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    textAlign: 'left'
  },
  sidebarItemActive: {
    background: 'linear-gradient(135deg, #FFB800 0%, #FFA000 100%)',
    color: 'white',
    border: '2px solid #FFB800',
    boxShadow: '0 4px 12px rgba(255, 184, 0, 0.3)'
  },
  quickInfo: {
    background: '#FFF9E6',
    border: '2px solid #FFB800',
    borderRadius: '12px',
    padding: '16px',
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start'
  },
  quickInfoText: {
    fontSize: '13px',
    color: '#666',
    lineHeight: '1.5',
    margin: 0
  },

  // Main Content
  mainContent: {
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    minHeight: '800px'
  },
  contentArea: {
    padding: '48px'
  },
  contentHeader: {
    borderBottom: '3px solid #133E97',
    paddingBottom: '24px',
    marginBottom: '40px'
  },
  contentTitle: {
    fontSize: '36px',
    fontWeight: '800',
    color: '#1a202c',
    marginBottom: '8px'
  },
  lastUpdated: {
    fontSize: '14px',
    color: '#718096',
    fontStyle: 'italic'
  },
  contentBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '32px'
  },
  section: {
    marginBottom: '24px'
  },
  sectionTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#133E97',
    marginBottom: '16px',
    paddingTop: '8px'
  },
  sectionContent: {
    fontSize: '16px',
    color: '#4a5568',
    lineHeight: '1.8',
    marginBottom: '16px'
  },
  subsection: {
    marginTop: '20px',
    marginLeft: '24px',
    paddingLeft: '20px',
    borderLeft: '3px solid #FFB800'
  },
  subsectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#2d3748',
    marginBottom: '12px'
  },
  subsectionContent: {
    fontSize: '15px',
    color: '#4a5568',
    lineHeight: '1.7'
  },

  // Contact CTA
  contactCTA: {
    marginTop: '60px',
    padding: '40px',
    background: 'linear-gradient(135deg, #f0f4ff 0%, #e6f0ff 100%)',
    borderRadius: '16px',
    border: '2px solid #133E97',
    textAlign: 'center'
  },
  ctaTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#133E97',
    marginTop: '16px',
    marginBottom: '8px'
  },
  ctaText: {
    fontSize: '16px',
    color: '#4a5568',
    marginBottom: '24px'
  },
  ctaButton: {
    background: 'linear-gradient(135deg, #133E97 0%, #1a52c9 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    padding: '14px 32px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 12px rgba(19, 62, 151, 0.3)'
  }
};

export default LegalPolicies;
